/**
 * ScoreDisplay - Points and Streak Display Component
 * 
 * Shows the participant's current score and streak.
 * Includes animations for points earned and streak bonuses.
 * 
 * Features:
 * - Animated score counter
 * - Streak fire icon with multiplier
 * - Points earned popup animation
 * - Gamification badges
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../../context/ThemeContext';

/**
 * ScoreDisplay component
 * @param {number} totalScore - Current total score
 * @param {number} currentStreak - Current answer streak
 * @param {number} lastPointsEarned - Points from last answer (for animation)
 * @param {boolean} pointsAwarded - Whether points were just awarded
 * @param {boolean} showStreak - Whether to show streak indicator
 * @param {string} size - Size variant: 'sm' | 'md' | 'lg'
 */
export function ScoreDisplay({
  totalScore = 0,
  currentStreak = 0,
  lastPointsEarned = null,
  pointsAwarded = false,
  showStreak = true,
  size = 'md',
}) {
  const { darkMode } = useTheme();
  const [showPointsPopup, setShowPointsPopup] = useState(false);

  // Size variants
  const sizeClasses = {
    sm: {
      container: 'px-3 py-1.5',
      scoreText: 'text-lg',
      label: 'text-xs',
      streak: 'text-sm',
      popup: 'text-sm',
    },
    md: {
      container: 'px-4 py-2',
      scoreText: 'text-2xl',
      label: 'text-xs',
      streak: 'text-base',
      popup: 'text-lg',
    },
    lg: {
      container: 'px-6 py-3',
      scoreText: 'text-3xl',
      label: 'text-sm',
      streak: 'text-lg',
      popup: 'text-xl',
    },
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  // Show points popup animation when points are awarded
  useEffect(() => {
    if (pointsAwarded && lastPointsEarned !== null && lastPointsEarned > 0) {
      setShowPointsPopup(true);
      const timer = setTimeout(() => setShowPointsPopup(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [pointsAwarded, lastPointsEarned]);

  // Get streak color based on streak length
  const getStreakColor = () => {
    if (currentStreak >= 10) return 'text-purple-500';
    if (currentStreak >= 7) return 'text-red-500';
    if (currentStreak >= 5) return 'text-orange-500';
    if (currentStreak >= 3) return 'text-yellow-500';
    return darkMode ? 'text-gray-400' : 'text-gray-500';
  };

  // Get streak label
  const getStreakLabel = () => {
    if (currentStreak >= 10) return 'LEGENDÄR!';
    if (currentStreak >= 7) return 'On Fire!';
    if (currentStreak >= 5) return 'Heiß!';
    if (currentStreak >= 3) return 'Streak';
    return '';
  };

  return (
    <div className="flex items-center gap-4 relative">
      {/* Score Display */}
      <div
        className={`
          ${sizes.container}
          rounded-xl
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
          border shadow-sm
          flex flex-col items-center
          transition-all duration-200
        `}
      >
        <span className={`${sizes.label} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Punkte
        </span>
        <span
          className={`
            ${sizes.scoreText} font-bold
            ${darkMode ? 'text-white' : 'text-gray-900'}
          `}
        >
          {totalScore.toLocaleString()}
        </span>

        {/* Points Popup Animation */}
        {showPointsPopup && lastPointsEarned > 0 && (
          <div
            className={`
              absolute -top-8 left-1/2 -translate-x-1/2
              ${sizes.popup} font-bold text-green-500
              animate-bounce
            `}
          >
            +{lastPointsEarned}
          </div>
        )}
      </div>

      {/* Streak Display */}
      {showStreak && currentStreak > 0 && (
        <div
          className={`
            ${sizes.container}
            rounded-xl
            ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
            border shadow-sm
            flex items-center gap-2
            ${currentStreak >= 5 ? 'animate-pulse' : ''}
          `}
        >
          {/* Fire Icon */}
          <svg
            className={`w-5 h-5 ${getStreakColor()}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
              clipRule="evenodd"
            />
          </svg>

          <div className="flex flex-col">
            <span className={`${sizes.streak} font-bold ${getStreakColor()}`}>
              {currentStreak}x
            </span>
            {getStreakLabel() && (
              <span className={`text-xs ${getStreakColor()}`}>
                {getStreakLabel()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact score badge for inline use
 */
export function ScoreBadge({ score, className = '' }) {
  const { darkMode } = useTheme();

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-1 rounded-full
        ${darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}
        font-medium text-sm
        ${className}
      `}
    >
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      {score.toLocaleString()}
    </span>
  );
}

/**
 * Streak flame badge
 */
export function StreakBadge({ streak, className = '' }) {
  const { darkMode } = useTheme();

  if (streak <= 0) return null;

  const getColor = () => {
    if (streak >= 10) return darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700';
    if (streak >= 7) return darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700';
    if (streak >= 5) return darkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-700';
    if (streak >= 3) return darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700';
    return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600';
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-1 rounded-full
        ${getColor()}
        font-medium text-sm
        ${streak >= 5 ? 'animate-pulse' : ''}
        ${className}
      `}
    >
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
          clipRule="evenodd"
        />
      </svg>
      {streak}x
    </span>
  );
}

export default ScoreDisplay;
